import { UserRole } from 'types';

import { TEACHER_CARD_ROLES } from '../Palette/paletteRoles';

/**
 * ТЗ 0114 (#158) — чистая модель реквизитов преподавателя (кто выставляет
 * фактуры) и финансовых условий заявки. Без React — проверяется jest-ом.
 */

/**
 * Кто видит и правит реквизиты и условия — зеркало `TEACHER_TERMS_ROLES`
 * бэкенда (`teachers_query.py`): роли карточки преподавателя + FinanceManager.
 * FinanceManager видит их и на вкладке «Финансы» группы, и в досье
 * преподавателя (публичная часть + условия).
 */
export const TEACHER_TERMS_ROLES: UserRole[] = [
  ...TEACHER_CARD_ROLES,
  UserRole.FINANCIER,
];

export const canManageTeacherTerms = (roles: readonly string[]): boolean =>
  roles.some((role) => (TEACHER_TERMS_ROLES as string[]).includes(role));

export const BILLING_KINDS = ['jdg', 'company', 'private'] as const;
export type BillingKind = typeof BILLING_KINDS[number];

/** Виды с NIP; у частного лица NIP нет (сервер откажет `not_allowed`). */
export const kindHasNip = (kind: string): boolean =>
  kind === 'jdg' || kind === 'company';

export const VAT_MODES = ['standard', 'exempt', 'not_vat_payer'] as const;
export type VatMode = typeof VAT_MODES[number];

/** Ставки VAT, которые принимает сервер (`TeacherBillingEntity.VAT_RATES`). */
export const VAT_RATES = [23, 8, 5, 0] as const;

export const RATE_UNITS = ['lesson', 'hour', 'course'] as const;
export type RateUnit = typeof RATE_UNITS[number];

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

/** NIP — только цифры (префикс `PL`, пробелы и дефисы отбрасываются). */
export const nipDigits = (value: string): string =>
  (value || '').replace(/\D/g, '');

/** Контрольная сумма NIP — как `esign/services/validators.nip_is_valid`. */
export const nipIsValid = (value: string): boolean => {
  const digits = nipDigits(value);
  if (digits.length !== 10) return false;
  const sum = NIP_WEIGHTS.reduce(
    (acc, weight, index) => acc + Number(digits[index]) * weight,
    0,
  );
  return sum % 11 === Number(digits[9]);
};

/** Подсказка под полем NIP по ходу ввода: пусто / мало цифр / ошибка / ок. */
export type NipState = 'empty' | 'short' | 'invalid' | 'ok';

export const nipState = (value: string): NipState => {
  const digits = nipDigits(value);
  if (!digits) return 'empty';
  if (digits.length < 10) return 'short';
  return nipIsValid(digits) ? 'ok' : 'invalid';
};

/** «123-456-32-18» — NIP в привычной записи; не 10 цифр — как есть. */
export const formatNip = (value: string): string => {
  const d = nipDigits(value);
  if (d.length !== 10) return value;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8)}`;
};

export interface IVatLike {
  vatMode: string;
  vatRate?: number | null;
}

/** i18n-ключ и параметры подписи VAT («VAT 23 %», «zw.», «не плательщик»). */
export const vatLabel = (
  entity: IVatLike,
): { key: string; rate?: number } | null => {
  if (entity.vatMode === 'standard' && entity.vatRate !== null) {
    return { key: 'teacherBilling.vat.rate', rate: entity.vatRate ?? 0 };
  }
  if (entity.vatMode === 'exempt') return { key: 'teacherBilling.vat.exempt' };
  if (entity.vatMode === 'not_vat_payer') {
    return { key: 'teacherBilling.vat.notPayer' };
  }
  return null;
};

/**
 * i18n-ключ отказа сервера по полю и коду (`nip invalid`, `vat_rate
 * invalid`, …); незнакомый — `null` (текст сервера).
 */
const ERROR_KEYS: Record<string, string> = {
  'nip:invalid': 'teacherBilling.errors.nipInvalid',
  'nip:not_allowed': 'teacherBilling.errors.nipNotAllowed',
  'nip:taken': 'teacherBilling.errors.nipTaken',
  'vat_rate:invalid': 'teacherBilling.errors.vatRateInvalid',
  'legal_name:required': 'teacherBilling.errors.nameRequired',
  'legal_name:too_long': 'teacherBilling.errors.nameTooLong',
  'kind:invalid': 'teacherBilling.errors.kindInvalid',
  'vat_mode:invalid': 'teacherBilling.errors.vatModeInvalid',
  'billing_entity_id:not_teachers': 'teacherBilling.errors.notTeachers',
  'rate_unit:invalid': 'teacherBilling.errors.rateUnitInvalid',
  'id:inactive': 'teacherBilling.errors.inactive',
};

/** Поле ответа сервера — в snake_case (бывает и camelCase). */
export const billingErrorField = (field: string): string =>
  (field || '').replace(/([A-Z])/g, '_$1').toLowerCase();

export const billingErrorKey = (error: {
  field?: string | null;
  code?: string | null;
}): string | null => {
  const field = billingErrorField(error.field || '');
  const code = (error.code || '').toLowerCase();
  if (code === 'does_not_exist') return 'teacherBilling.errors.notFound';
  return ERROR_KEYS[`${field}:${code}`] ?? null;
};

/** Коды «не заполнено» условий заявки (`ApplicationType.termsMissing`). */
export const TERMS_MISSING_CODES = [
  'service_price',
  'currency',
  'rate_is_gross',
  'billing_entity',
  'vat_mode',
] as const;

export const termsMissingKey = (code: string): string =>
  (TERMS_MISSING_CODES as readonly string[]).includes(code)
    ? `teacherBilling.missing.${code}`
    : code;

/** Выбор net / gross в форме: `null` — «не указано» (у старых заявок). */
export type GrossChoice = 'net' | 'gross' | '';

export const grossChoice = (value: boolean | null | undefined): GrossChoice => {
  if (value === true) return 'gross';
  if (value === false) return 'net';
  return '';
};

export const grossValue = (choice: GrossChoice): boolean | null => {
  if (choice === 'gross') return true;
  if (choice === 'net') return false;
  return null;
};

export interface IApplicationTermsDraft {
  rateUnit: string;
  rateIsGross: GrossChoice;
  billingEntityId: string;
}

/**
 * Переменные `setApplicationFinanceTerms`: контрагент снят — явный
 * `clearBillingEntity` (иначе «не передан» = «не менять»); net/gross «не
 * указано» не отправляется (сервер оставит как есть).
 */
export const applicationTermsVariables = (
  applicationId: string,
  draft: IApplicationTermsDraft,
  extra: { servicePrice?: number | null; currency?: string | null } = {},
): Record<string, unknown> => {
  const gross = grossValue(draft.rateIsGross);
  return {
    applicationId,
    rateUnit: draft.rateUnit || null,
    ...(gross === null ? {} : { rateIsGross: gross }),
    ...(draft.billingEntityId
      ? { billingEntityId: draft.billingEntityId }
      : { clearBillingEntity: true }),
    ...(extra.servicePrice !== undefined && extra.servicePrice !== null
      ? { servicePrice: extra.servicePrice }
      : {}),
    ...(extra.currency ? { currency: extra.currency } : {}),
  };
};
